import React, { useEffect, useState } from "react";
import { formatDateTime } from "../../util/dateUtils";
import { Tag, Typography, Button, Modal, Card, Divider, message, Avatar, List, Descriptions, Input, Space, Image } from "antd";
import DataTable from "../../component/ui/DataTable";
import Breadcrumbs from "../../component/Breadcrumbs";
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import OrdersServ from "../../service/OrdersServ";
import EmptyState from "../../util/EmptyState";
import { getImageUrl } from "../../component/SafeImage";
import { getOrderRowClass } from "../../util/statusHighlight";

const { Title, Text } = Typography;

const STATUS_COLORS = {
    requested: 'orange',
    approved: 'blue',
    rejected: 'red',
    picked_up: 'purple',
    refund_completed: 'green',
    RETURN_REQUESTED: 'orange',
    RETURN_APPROVED_BY_ADMIN: 'blue',
    RETURN_REJECTED_BY_ADMIN: 'red',
    RETURN_PICKUP_PENDING: 'blue',
    RETURN_REJECTED_BY_SELLER: 'red',
    RETURN_PICKUP_REJECTED: 'red',
    RETURN_PICKED_UP: 'purple',
    RETURN_IN_TRANSIT: 'purple',
    RETURN_DELIVERED_TO_SELLER: 'cyan',
    RETURN_COMPLETED: 'green',
    WAITING_FOR_ADMIN_REFUND: 'orange',
    REFUND_PENDING: 'orange',
    REFUND_PROCESSING: 'orange',
    REFUND_COMPLETED: 'green'
};

const ReturnManagement = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [sellerComment, setSellerComment] = useState("");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const res = await OrdersServ.getSellerReturns();
            setRequests(Array.isArray(res?.data) ? res.data : (res?.data?.data || []));
        } catch (error) {
            console.error("Fetch Error:", error);
            message.error("Failed to load return requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setSellerComment(req.sellerComment || "");
        setIsModalOpen(true);
    };

    const handleUpdateStatus = async (status) => {
        if (!selectedRequest) return;
        if (status === 'rejected' && !sellerComment.trim()) {
            message.warning("Please provide a comment for rejection");
            return;
        }

        try {
            setActionLoading(true);
            await OrdersServ.updateReturnStatus(selectedRequest._id, status, sellerComment);
            message.success(`Return request status updated to: ${status.replace(/_/g, ' ')}`);
            setIsModalOpen(false);
            fetchRequests();
        } catch (error) {
            message.error(error.message || "Failed to update status");
        } finally {
            setActionLoading(false);
        }
    };

    const columns = [
        {
            title: "Request ID",
            dataIndex: "returnId",
            key: "returnId",
            width: 130,
            render: (text) => <Text type="secondary" style={{ fontSize: 12 }}>{text}</Text>
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 90,
            render: (type) => <Tag color={type === 'cancel' ? 'volcano' : 'blue'}>{type?.toUpperCase()}</Tag>
        },
        {
            title: "Customer",
            dataIndex: "user",
            key: "user",
            render: (user) => <Text strong>{user?.firstName} {user?.lastName}</Text>
        },
        {
            title: "Amount",
            dataIndex: "refundAmount",
            key: "refundAmount",
            width: 100,
            align: "right",
            render: (amount) => <Text strong style={{ color: '#FF8800' }}>₹{amount}</Text>
        },

        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 130,
            render: (date) => {
                const d = new Date(date);
                return (
                    <div className="pcu-flex pcu-flex-column">
                        <Text>{d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</Text>
                        <Text type="secondary" className="pcu-font-12">
                            {d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                        </Text>
                    </div>
                );
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                const normalizedStatus = (status || "").trim().toUpperCase();
                const readableStatus = status ? status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : "";
                return <Tag color={STATUS_COLORS[normalizedStatus] || 'default'} style={{ borderRadius: 6, fontWeight: 600 }}>{readableStatus}</Tag>;
            }
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
                    Manage
                </Button>
            )
        }
    ];

    return (
        <div className="product-option-container pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom">
                    <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                        Return & Cancellation Management
                    </h1>
                </div>
                <div className="pcu-padding-24">
                    {isMobile ? (
                        <div className="pcu-flex pcu-flex-column pcu-gap-12">
                            {loading ? (
                                <div className="pcu-text-center pcu-padding-40">Loading...</div>
                            ) : requests.length === 0 ? (
                                <EmptyState
                                    title="No Requests Found"
                                    description="You have no return or cancellation requests at the moment."
                                    gradient="orange"
                                    showAction={false}
                                    icon="inbox"
                                />
                            ) : (
                                requests.map(req => {
                                    const d = new Date(req.createdAt);

                                    return (
                                        <Card
                                            key={req._id}
                                            size="small"
                                            className={`pcu-radius-8 pcu-shadow-sm pcu-cursor-pointer ${getOrderRowClass(req.status)}`}
                                            onClick={() => handleViewDetails(req)}
                                        >
                                            <div className="pcu-flex pcu-justify-between pcu-items-start pcu-margin-bottom-12">
                                                <div>
                                                    <Text strong className="pcu-font-13 pcu-color-muted">#{req.returnId}</Text>
                                                    <div className="pcu-margin-top-4">
                                                        <Tag color={req.type === 'cancel' ? 'volcano' : 'blue'} className="pcu-font-11">
                                                            {req.type?.toUpperCase()}
                                                        </Tag>
                                                    </div>
                                                </div>
                                                <Tag color={STATUS_COLORS[(req.status || "").trim().toUpperCase()] || 'default'}>
                                                    {req.status?.toUpperCase()?.replace(/_/g, " ")}
                                                </Tag>
                                            </div>

                                            <Divider className="pcu-margin-y-8" />

                                            <div className="pcu-flex pcu-justify-between pcu-margin-bottom-8">
                                                <div>
                                                    <Text type="secondary" className="pcu-font-12 pcu-block">Customer</Text>
                                                    <Text strong className="pcu-font-14">
                                                        {req.user?.firstName} {req.user?.lastName}
                                                    </Text>
                                                </div>
                                                <div className="pcu-text-right">
                                                    <Text type="secondary" className="pcu-font-12 pcu-block">Amount</Text>
                                                    <Text strong className="pcu-font-14 pcu-color-orange">₹{req.refundAmount}</Text>
                                                </div>
                                            </div>

                                            <div className="pcu-margin-top-8">
                                                <Text type="secondary" className="pcu-font-11">
                                                    {d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    {' • '}
                                                    {d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </Text>
                                            </div>
                                        </Card>
                                    );
                                })
                            )}
                        </div>
                    ) : (
                        <div className="pcu-border-top pcu-margin-x-n24">
                            <DataTable
                                columns={columns}
                                data={requests}
                                rowKey="_id"
                                locale={{
                                    emptyText: (
                                        <EmptyState
                                            title="No Requests Found"
                                            description="You have no return or cancellation requests at the moment."
                                            gradient="orange"
                                            showAction={false}
                                            icon="inbox"
                                        />
                                    )
                                }}
                                loading={loading}
                                pagination={{ 
                                    pageSize: 10,
                                    onChange: (page) => {
                                        const pageContent = document.querySelector('.page-content');
                                        if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                        else window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                }}
                                rowClassName={(record) => getOrderRowClass(record.status)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Modal
                title={null}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={540}
                centered
                styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: 'hidden', padding: 0 } }}
            >
                {selectedRequest && (
                    <>
                        {/* Premium Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                            padding: '20px 24px 16px',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,129,16,0.15)' }} />
                            <div style={{ position: 'absolute', bottom: -20, left: '40%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
                                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,129,16,0.2)', border: '1px solid rgba(255,129,16,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                                    📦
                                </div>
                                <div>
                                    <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>Return Request #{selectedRequest.returnId}</div>
                                    <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 }}>Order #{selectedRequest.order?.orderId}</div>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '16px', maxHeight: '60vh', overflowY: 'auto', background: '#f8f9fc' }}>
                            <Descriptions bordered size="small" column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', marginBottom: 16, border: '1px solid #e5e7eb' }}>
                                <Descriptions.Item label="Customer">{selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</Descriptions.Item>
                                <Descriptions.Item label="Shop">{selectedRequest.shop?.name || selectedRequest.order?.shopId?.name || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Type"><span style={{ whiteSpace: 'nowrap' }}>{selectedRequest.type?.toUpperCase() || 'RETURN'}</span></Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={STATUS_COLORS[(selectedRequest.status || "").trim().toUpperCase()] || 'default'} style={{ borderRadius: 6, fontWeight: 600 }}>
                                        {selectedRequest.status ? selectedRequest.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : ""}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>

                            <Title level={5} style={{ fontSize: 16, marginBottom: 12 }}>Returned Items</Title>

                            {(selectedRequest.returnItems?.length ? selectedRequest.returnItems : (selectedRequest.items || [])).map((item, idx) => (
                                <div key={idx} style={{
                                    marginBottom: 8,
                                    background: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 12,
                                    padding: '10px 12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    {/* Item Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                        <Avatar src={getImageUrl(item.productImage || item.product?.imageUrl || item.imageUrl)} size={36} shape="square" style={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.05)' }} />
                                        <div>
                                            <Text strong style={{ fontSize: 13, color: '#111827' }}>{item.productName || item.name}</Text>
                                            <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 500, marginTop: 2 }}>Qty: {item.returnQty || item.quantity} <span style={{ color: '#e5e7eb', margin: '0 6px' }}>|</span> <span style={{ color: '#ff8110', fontWeight: 600 }}>₹{(item.price || 0) * (item.returnQty || item.quantity || 1)}</span></div>
                                        </div>
                                    </div>

                                    {/* Item-level Return Reason */}
                                    {item.returnReason && (
                                        <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fafafa', borderRadius: 8, borderLeft: '3px solid #ff8110' }}>
                                            <Text type="secondary" style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reason: </Text>
                                            <Text style={{ fontSize: 12, color: '#1f2937', fontWeight: 500 }}>{item.returnReason}</Text>
                                            {item.description && (
                                                <div style={{ marginTop: 2, color: '#6b7280', fontSize: 11 }}>{item.description}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* Item-level Evidence Images */}
                                    {item.images && item.images.length > 0 && (
                                        <div>
                                            <Text type="secondary" style={{ display: 'block', fontSize: 10, fontWeight: 600, marginBottom: 8, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Evidence</Text>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                {item.images.map((img, imgIdx) => {
                                                    let imgSrc = typeof img === 'string' ? img : (img?.url || img?.uri || '');
                                                    imgSrc = imgSrc.replace(/\\/g, '/');
                                                    if (imgSrc.includes('uploads/')) {
                                                        imgSrc = '/uploads/' + imgSrc.split('uploads/').pop();
                                                    }
                                                    return (
                                                        <Image
                                                            key={imgIdx}
                                                            src={getImageUrl(imgSrc)}
                                                            width={44}
                                                            height={44}
                                                            style={{ borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.06)' }}
                                                            fallback="https://via.placeholder.com/60?text=No+Img"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {['APPROVED', 'RETURN_APPROVED_BY_ADMIN'].includes(selectedRequest?.status?.toUpperCase()) && (
                                <div style={{ marginTop: 12 }}>
                                    <Text strong style={{ fontSize: 14 }}>Seller Note:</Text>
                                    <Input.TextArea
                                        value={sellerComment}
                                        onChange={e => setSellerComment(e.target.value)}
                                        rows={3}
                                        style={{ marginTop: 8, borderRadius: 8 }}
                                        placeholder="Optional note for your records..."
                                    />
                                </div>
                            )}

                            {selectedRequest.sellerComment && !['APPROVED', 'RETURN_APPROVED_BY_ADMIN'].includes(selectedRequest?.status?.toUpperCase()) && (
                                <div style={{ marginTop: 12, padding: '12px', background: '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                                    <Text strong style={{ fontSize: 12, color: '#4338ca' }}>Your Note:</Text>
                                    <div style={{ fontSize: 13, color: '#312e81', marginTop: 4 }}>{selectedRequest.sellerComment}</div>
                                </div>
                            )}
                            {selectedRequest.rejectionReason && (
                                <div style={{ marginTop: 12, padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                    <Text strong style={{ fontSize: 12, color: '#b91c1c' }}>Rejection Details ({selectedRequest.rejectionStage || 'Unknown Stage'}):</Text>
                                    <div style={{ fontSize: 13, color: '#991b1b', marginTop: 4 }}>{selectedRequest.rejectionReason}</div>
                                    {selectedRequest.rejectionDate && (
                                        <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>{new Date(selectedRequest.rejectionDate).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Premium Footer */}
                        <div style={{ padding: '0 16px 16px', background: '#f8f9fc' }}>
                            {/* Summary Strip */}
                            <div style={{
                                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
                                padding: '10px 14px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                    <div>
                                        <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Items</div>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>{(selectedRequest.returnItems?.length ? selectedRequest.returnItems : (selectedRequest.items || [])).length}</div>
                                    </div>
                                    <div style={{ width: '1px', height: '32px', background: '#e5e7eb' }} />
                                    <div>
                                        <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Total Refund</div>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#16a34a' }}>₹{selectedRequest.refundAmount}</div>
                                    </div>
                                </div>
                                <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                    💳
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                                <Button
                                    onClick={() => setIsModalOpen(false)}
                                    style={{ height: '36px', borderRadius: '8px', fontWeight: 600, width: '100px' }}
                                >
                                    Close
                                </Button>
                                {['APPROVED', 'RETURN_APPROVED_BY_ADMIN'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <Button
                                        type="primary"
                                        loading={actionLoading}
                                        onClick={() => handleUpdateStatus('approved')}
                                        style={{ background: '#16a34a', borderColor: '#16a34a', height: '36px', borderRadius: '8px', fontWeight: 600, width: '140px' }}
                                    >
                                        ✓ Accept Return
                                    </Button>
                                )}
                                {['RETURN_REQUESTED', 'RETURN_PENDING', 'REQUESTED', 'PENDING'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <div style={{ display: 'flex', alignItems: 'center', background: '#fffbeb', padding: '0 16px', borderRadius: '8px', border: '1px solid #fef3c7', height: '36px', color: '#d97706', fontWeight: 600, fontSize: 13 }}>
                                        ⏳ Waiting for Admin Approval...
                                    </div>
                                )}
                                {['RETURN_DELIVERED_TO_SELLER'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <Button
                                        type="primary"
                                        loading={actionLoading}
                                        onClick={() => handleUpdateStatus('waiting_for_admin_refund')}
                                        style={{ background: '#16a34a', borderColor: '#16a34a', height: '36px', borderRadius: '8px', fontWeight: 600, width: '220px' }}
                                    >
                                        ✓ Verify Receipt
                                    </Button>
                                )}
                                {['WAITING_FOR_ADMIN_REFUND'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <div style={{ display: 'flex', alignItems: 'center', background: '#fffbeb', padding: '0 16px', borderRadius: '8px', border: '1px solid #fef3c7', height: '36px', color: '#d97706', fontWeight: 600, fontSize: 13 }}>
                                        ⏳ Waiting for Admin Refund...
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default ReturnManagement;

